import { LitElement, html, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { styles } from "./styles";
import type { Language, SocialLink, LegalLink, Breadcrumb } from "@ligonier/shared";
import {
  defaultSections,
  defaultLanguages,
  defaultSocialLinks,
  defaultLegalLinks,
} from "@ligonier/shared";
import {
  getCurrentYear,
  createEmailSignupEvent,
  fetchFooterNavigation,
  booleanStringConverter,
  sanitizeHref,
} from "@ligonier/shared";
import { renderStoreFooter } from "./templates/store-footer";
import { renderCalloutFooter, DEFAULT_DONATE_URL } from "./templates/callout-footer";
import { renderEmailFooter } from "./templates/email-footer";
import { renderBreadcrumbs } from "./templates/breadcrumbs";
import { renderLanguages } from "./templates/languages";
import { renderFooterGroups } from "./templates/footer-groups";
import { renderDivider } from "./templates/divider";
import { renderTertiary } from "./templates/tertiary";
import type { NavigationSection } from "@ligonier/shared";

@customElement("ligonier-footer")
export class LigonierFooter extends LitElement {
  // booleanStringConverter returns null on attribute removal; useDefault makes
  // Lit restore each property's declared default instead of forcing a boolean
  // (removal would otherwise flip show-store-footer ON — inverted semantics).
  @property({
    converter: booleanStringConverter,
    attribute: "show-store-footer",
    useDefault: true,
  })
  showStoreFooter = false;

  @property({
    converter: booleanStringConverter,
    attribute: "show-callout",
    useDefault: true,
  })
  showCallout = true;

  // Per-page breadcrumb trail. Because breadcrumbs are route-specific, the
  // consuming app passes them in as a JSON attribute; the footer renders them
  // when the trail is non-empty.
  //
  // The attribute is named `breadcrumbs-json` (not `breadcrumbs`) on purpose:
  // React 19 assigns JSX props that match an element property directly to the
  // PROPERTY, bypassing Lit's attribute JSON converter — a raw string on the
  // array property would crash rendering. The dashed attribute name can never
  // collide with the property, so JSON.stringify wiring stays safe on React 19.
  //
  // useDefault: Lit's Array converter yields null for malformed JSON or
  // attribute removal; useDefault falls back to [] instead of null.
  @property({ type: Array, attribute: "breadcrumbs-json", useDefault: true })
  breadcrumbs: Array<Breadcrumb> = [];

  @property({ type: String, attribute: "donate-url" })
  donateUrl = DEFAULT_DONATE_URL;

  @property({ type: Array })
  sections: Array<NavigationSection> = [...defaultSections];

  @property({ type: Array })
  languages: Array<Language> = defaultLanguages;

  @property({ type: Array })
  socialLinks: Array<SocialLink> = defaultSocialLinks;

  @property({ type: Array })
  legalLinks: Array<LegalLink> = defaultLegalLinks;

  static styles = styles;

  private sectionsAbortController?: AbortController;
  private breadcrumbLdScript?: HTMLScriptElement;

  /**
   * Defensive view of `breadcrumbs`: a consumer can set the property directly
   * from JS to null/garbage (bypassing the attribute converter), and a bad
   * trail must degrade to "no breadcrumbs" — never crash the whole footer.
   */
  private get safeBreadcrumbs(): Array<Breadcrumb> {
    return Array.isArray(this.breadcrumbs) ? this.breadcrumbs : [];
  }

  connectedCallback() {
    super.connectedCallback();
    // The footer is API-authoritative for its navigation sections (including the
    // dynamic Events section), mirroring the header. Sites embed a bare
    // <ligonier-footer> and the component fetches its own content so every site
    // stays consistent. The static defaults remain as the fallback shown until
    // the fetch resolves (or if it fails / the endpoint is unavailable).
    void this.fetchSections();
    // Re-establish the JSON-LD after a DOM move: disconnectedCallback removes
    // the script, and a reconnect with unchanged properties schedules no update,
    // so updated() alone would never re-inject it.
    this.updateBreadcrumbStructuredData();
  }

  disconnectedCallback() {
    this.sectionsAbortController?.abort();
    this.sectionsAbortController = undefined;
    this.breadcrumbLdScript?.remove();
    this.breadcrumbLdScript = undefined;
    super.disconnectedCallback();
  }

  protected updated(changedProperties: PropertyValues<this>) {
    if (changedProperties.has("breadcrumbs" as keyof LigonierFooter)) {
      this.updateBreadcrumbStructuredData();
    }
  }

  /**
   * Emit a BreadcrumbList JSON-LD script into the document <head> so search
   * engines can read the breadcrumb trail for rich results.
   *
   * Structured data is injected into the light DOM (head) rather than rendered
   * as microdata inside the breadcrumbs, because crawlers do not reliably parse
   * structured data inside a web component's shadow DOM. JSON-LD is also
   * Google's recommended format.
   *
   * Emission is skipped when:
   * - the page already declares its own BreadcrumbList (route-level JSON-LD,
   *   e.g. learn article and careers posting routes) — two conflicting trails
   *   are worse for rich results than one;
   * - any non-final crumb has no resolvable URL — Google requires `item` on
   *   every ListItem except the last, so the list would be invalid.
   */
  private updateBreadcrumbStructuredData() {
    if (typeof document === "undefined") return;

    const removeScript = () => {
      this.breadcrumbLdScript?.remove();
      this.breadcrumbLdScript = undefined;
    };

    const breadcrumbs = this.safeBreadcrumbs;
    if (!this.isConnected || breadcrumbs.length === 0) {
      removeScript();
      return;
    }

    // The page (route meta) may already own breadcrumb structured data.
    const hasForeignBreadcrumbList = Array.from(
      document.querySelectorAll(
        'script[type="application/ld+json"]:not([data-ligonier-footer-breadcrumbs])',
      ),
    ).some((script) => script.textContent?.includes('"BreadcrumbList"'));
    if (hasForeignBreadcrumbList) {
      removeScript();
      return;
    }

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    // Same sanitation as the rendered anchors, then resolved against the
    // CURRENT origin (matching how the browser resolves the visible links).
    const toItemUrl = (link?: string): string | undefined => {
      if (!link) return undefined;
      const sanitized = sanitizeHref(link);
      if (sanitized === "#") return undefined;
      try {
        return new URL(sanitized, origin || undefined).href;
      } catch {
        return undefined;
      }
    };

    const items = breadcrumbs.map((crumb, index) => {
      const item = toItemUrl(crumb.link);
      return {
        "@type": "ListItem",
        position: index + 1,
        name: crumb.label,
        // The current page (last crumb) may omit `item` per schema.org.
        ...(item ? { item } : {}),
      };
    });

    // Google rejects a BreadcrumbList where a non-final item lacks `item`.
    const isValidList = items
      .slice(0, -1)
      .every((entry) => "item" in entry);
    if (!isValidList) {
      removeScript();
      return;
    }

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items,
    };

    if (!this.breadcrumbLdScript) {
      this.breadcrumbLdScript = document.createElement("script");
      this.breadcrumbLdScript.type = "application/ld+json";
      this.breadcrumbLdScript.setAttribute(
        "data-ligonier-footer-breadcrumbs",
        "",
      );
      document.head.appendChild(this.breadcrumbLdScript);
    }
    // Escape "<" so a CMS-driven label containing "</script>" can never
    // terminate the script element when the document is serialized/re-parsed
    // (snapshot pipelines, SEO tools). < parses back to the same JSON.
    this.breadcrumbLdScript.textContent = JSON.stringify(jsonLd).replace(
      /</g,
      "\\u003c",
    );
  }

  private async fetchSections() {
    this.sectionsAbortController?.abort();
    const abortController = new AbortController();
    this.sectionsAbortController = abortController;

    try {
      const sections = await fetchFooterNavigation(abortController.signal);

      if (!this.isConnected || this.sectionsAbortController !== abortController) {
        return;
      }

      // Only replace the defaults when the API returns usable sections, so a
      // transient failure or empty payload never blanks out the footer.
      if (Array.isArray(sections) && sections.length > 0) {
        this.sections = sections;
      }
    } catch (error) {
      const isAbortError =
        abortController.signal.aborted ||
        (error instanceof DOMException && error.name === "AbortError");

      if (!isAbortError) {
        console.warn("Failed to fetch footer navigation:", error);
      }
    } finally {
      if (this.sectionsAbortController === abortController) {
        this.sectionsAbortController = undefined;
      }
    }
  }

  private handleEmailSubmit = (e: Event) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const email = formData.get("email") as string;

    this.dispatchEvent(createEmailSignupEvent(email));

    alert(`Email signup submitted: ${email}`);
    form.reset();
  };

  render() {
    const currentYear = getCurrentYear();
    const breadcrumbs = this.safeBreadcrumbs;

    return html`
      <footer>
        ${this.showStoreFooter ? renderStoreFooter() : ""}
        ${this.showCallout ? renderCalloutFooter(this.donateUrl) : ""}
        ${this.showCallout ? renderEmailFooter(this.handleEmailSubmit) : ""}
        ${breadcrumbs.length > 0 ? renderBreadcrumbs(breadcrumbs) : ""}
        ${renderFooterGroups(this.sections)} ${renderLanguages(this.languages)}
        ${renderDivider()}
        ${renderTertiary(this.socialLinks, this.legalLinks, currentYear)}
      </footer>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ligonier-footer": LigonierFooter;
  }
}
